import React, { useEffect, useState } from 'react'
import { Box, Button, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppFilterPanel, AppPillToggle, KindPillsField, filterPanelGridSx, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import { pickupPresets } from 'Pages/DsRepricing/@services/dateRange'
import { DEFAULT_CUSTOM_DAYS, LOR_MAX, MAX_CUSTOM_DAYS, MAX_PICKUP_DAYS, clampPickup, spanDays } from '../@services/viewState'
import { SIMULATED_OPTIONS, UNIT_OPTIONS, WINDOW_OPTIONS, contextLine } from '../@services/windowLabel'


/** One office, from the account's catalogue (PMS codes): a `KindPillsField` with a single kind, the list under. */
function OfficeField({ value, offices, loading, onChange }) {
	const [query, setQuery] = useState('')
	const current = offices.find(o => o.code === value)
	const label = current ? `${current.code} · ${current.name}` : value || (loading ? 'Loading…' : 'No office')
	const shown = offices.filter(o => !query || `${o.code} ${o.name} ${o.city || ''}`.toLowerCase().includes(query.toLowerCase()))
	return <KindPillsField
		label="Office" allText={label} clearLabel="First office" pillsLabel="Office" on={!!current} labels={current ? [label] : []}
		under={current?.city ? current.city : null}
		kinds={[{ value: 'office', label: 'Office', note: offices.length ? `${offices.length}` : null }]} openTab={() => 'office'}
		renderKind={(tab, close) => <>
			{offices.length > 8 && <TextField size="small" fullWidth autoFocus placeholder="Find an office" value={query} onChange={e => setQuery(e.target.value)} sx={{ mb: 1 }}
				InputProps={{ startAdornment: <InputAdornment position="start"><IconSeeker icon="magnifying-glass" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/></InputAdornment> }}/>}
			<List dense disablePadding sx={{ maxHeight: 260, overflow: 'auto' }}>
				{shown.map(o => <ListItemButton key={o.code} selected={o.code === value} onClick={() => { onChange(o.code); close() }} sx={{ borderRadius: '8px' }}>
					<ListItemText primary={<><b>{o.code}</b> · {o.name}</>} secondary={o.city || null} primaryTypographyProps={{ fontSize: 13 }} secondaryTypographyProps={{ fontSize: 12 }}/>
				</ListItemButton>)}
				{!shown.length && <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, p: 1 }}>{loading ? 'Loading the offices…' : 'No office matches.'}</Typography>}
			</List>
		</>}
		onClear={() => { if (offices[0]) onChange(offices[0].code) }}
	/>
}

/** A whole number of days, committed on blur or Enter; outside the bounds the box goes red and nothing is emitted. */
function DaysField({ label, value, min = 1, max, onCommit, helper }) {
	const [draft, setDraft] = useState(String(value ?? ''))
	const [error, setError] = useState(false)
	useEffect(() => { setDraft(String(value ?? '')); setError(false) }, [value])
	const commit = () => {
		const n = Number(draft)
		if (!/^\d+$/.test(draft) || n < min || n > max) { setError(true); return }
		setError(false)
		if (n !== value) onCommit(n)
	}
	return <TextField size="small" fullWidth label={label} value={draft} error={error} helperText={error ? `${min} to ${max}` : helper}
		onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
		inputProps={{ inputMode: 'numeric', 'aria-label': label }}
		InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>days</Typography></InputAdornment> }}/>
}

/**
 * The band of Last changes (plan § 4.3): Window pills (+ Custom N) on the left, the simulated filter (v1.5: Include
 * counts simulated decisions like written ones, Exclude hides them) and the unit on the right; Office,
 * Rental length and Pick-up dates (≤ 31 days) as fields; Reset; the context line under. Every change applies at once.
 */
export default function Band({ view, offices, officesLoading, data, onChange, onReset }) {
	const [note, setNote] = useState(null)
	const today = new Date()
	const span = spanDays(view.pickup_from, view.pickup_to)

	const pickPickup = ({ from, to }) => {
		if (!from || !to) return
		const next = clampPickup(from, to)
		setNote(spanDays(from, to) > MAX_PICKUP_DAYS ? `Up to ${MAX_PICKUP_DAYS} pick-up dates: the range was cut at ${next.to}.` : null)
		onChange({ pickup_from: next.from, pickup_to: next.to })
	}

	return <AppFilterPanel
		toggles={<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
				<Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }}>Window</Typography>
				<AppPillToggle aria-label="Window" value={view.window} options={WINDOW_OPTIONS} onChange={w => onChange({ window: w, days: w === 'custom' ? (view.days || 7) : null })}/>
				{view.window === 'custom' && <Box sx={{ width: 150 }}>
					<DaysField label="Last N days" value={view.days || DEFAULT_CUSTOM_DAYS} min={1} max={MAX_CUSTOM_DAYS} onCommit={n => onChange({ days: n })}/>
				</Box>}
			</Box>
		</>}
		togglesRight={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
			<Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }}>Simulated</Typography>
			<AppPillToggle aria-label="Simulated decisions" value={view.simulated || 'include'} options={SIMULATED_OPTIONS} onChange={simulated => onChange({ simulated })}/>
			<Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }}>Prices</Typography>
			<AppPillToggle aria-label="Unit" value={view.unit} options={UNIT_OPTIONS} onChange={unit => onChange({ unit })}/>
		</Box>}
	>
		<Box sx={filterPanelGridSx(4)}>
			<OfficeField value={view.office} offices={offices} loading={officesLoading} onChange={office => onChange({ office })}/>
			<DaysField label="Rental length" value={view.lor} min={1} max={LOR_MAX} onCommit={lor => onChange({ lor })} helper="One rental length"/>
			<Box>
				<DateRangeField label="Pick-up dates" from={view.pickup_from} to={view.pickup_to} onChange={pickPickup} presets={pickupPresets(today)} width="100%" collapseSameDay/>
				<Typography sx={{ fontSize: 12, color: note ? dsColorNew.noteAmber : dsColorNew.textMuted, mt: 0.5 }}>{note || `${span} pick-up date${span === 1 ? '' : 's'} · up to ${MAX_PICKUP_DAYS}`}</Typography>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
				<Button variant="outlined" onClick={onReset} startIcon={<IconSeeker icon="rotate-left" sx={{ fontSize: 13 }}/>} sx={{ ...secondaryButtonSx, height: 40 }}>Reset</Button>
			</Box>
		</Box>
		{!!data && <Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 2 }}>{contextLine(data)}</Typography>}
	</AppFilterPanel>
}
